import React, { useRef, useState, useEffect, useCallback } from 'react';
import './CafeWhiteboard.css';

/**
 * CafeWhiteboard - Database-synced whiteboard with realtime collaboration
 * 
 * Props:
 * - roomId: UUID of the current room (required for sync)
 * - supabase: Supabase client instance
 * - userId: Current user's ID
 * - isPresenter: Whether current user can draw
 * - onEnterFocusMode: Callback to enter fullscreen mode
 * - layoutMode: 'normal' | 'presentation' | 'fullscreen'
 */
const CafeWhiteboard = ({ 
  roomId, 
  supabase, 
  userId, 
  isPresenter = true,
  onEnterFocusMode, 
  layoutMode = 'normal' 
}) => {
  const canvasRef = useRef(null);
  
  // Strokes from database (synced with all participants)
  const [strokes, setStrokes] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Current drawing state
  const [drawing, setDrawing] = useState(false);
  const currentStrokeRef = useRef([]); // Points being drawn
  const startRef = useRef(null); // Start point for shapes
  
  // Selection / dragging state (for shapes)
  const [selectedShapeId, setSelectedShapeId] = useState(null);
  const dragShapeRef = useRef(null); // { stroke, startMouse: {x,y} }
  
  // Tool state
  const [tool, setTool] = useState('pen');
  const [pointerPos, setPointerPos] = useState(null);
  const [remotePointers, setRemotePointers] = useState({}); // other users' pointers
  const pointerChannelRef = useRef(null);
  const isPointerDownRef = useRef(false); // for click+hold broadcast from audience
  const [color, setColor] = useState('#6e8efb');
  const [size, setSize] = useState(4); // pen / eraser size
  const [shapeSize, setShapeSize] = useState(4); // independent size for shapes
  const [expanded, setExpanded] = useState(false);
  const [showPenMenu, setShowPenMenu] = useState(false);
  const [showEraserMenu, setShowEraserMenu] = useState(false);
  const [showShapesMenu, setShowShapesMenu] = useState(false);
  const [fillShapes, setFillShapes] = useState(false);
  
  // Text box state
  const [textBox, setTextBox] = useState({ active: false, x: 40, y: 40, text: '', color: '#000000' });
  const draggingRef = useRef(false);
  const dragOffsetRef = useRef({ dx: 0, dy: 0 });
  const textDragRef = useRef({ active: false, dx: 0, dy: 0 });
  

  // Canvas dimensions
  const CANVAS_W = 1200;
  const CANVAS_H = 800;

  // Initialize canvas
  useEffect(() => {
    const c = canvasRef.current;
    if (!c) return;
    c.width = CANVAS_W;
    c.height = CANVAS_H;
  }, []);

  // Redraw all strokes on canvas
  const redrawCanvas = useCallback(() => {
    const c = canvasRef.current;
    if (!c) return;
    const ctx = c.getContext('2d');
    
    // Clear canvas with a clean white background (no theme shadows/textures)
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, CANVAS_W, CANVAS_H);
    
    // Draw all strokes
    strokes.forEach(stroke => {
      ctx.globalCompositeOperation = stroke.stroke_type === 'eraser' ? 'destination-out' : 'source-over';
      
      if (stroke.stroke_type === 'pen' || stroke.stroke_type === 'eraser') {
        const { points, color: sColor, size: sSize } = stroke.data;
        if (!points || points.length < 2) return;
        ctx.strokeStyle = stroke.stroke_type === 'eraser' ? 'rgba(0,0,0,1)' : sColor;
        ctx.lineWidth = sSize;
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';
        ctx.beginPath();
        ctx.moveTo(points[0].x, points[0].y);
        points.slice(1).forEach(pt => ctx.lineTo(pt.x, pt.y));
        ctx.stroke();
      } else if (stroke.stroke_type === 'shape') {
        const { shapeType, x0, y0, x1, y1, color: sColor, size: sSize, fill } = stroke.data;
        ctx.strokeStyle = sColor;
        ctx.fillStyle = sColor;
        ctx.lineWidth = sSize;
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';
        drawShapeOnCanvas(ctx, shapeType, x0, y0, x1, y1, { fill });
      } else if (stroke.stroke_type === 'text') {
        const { x, y, text, color: tColor, fontSize } = stroke.data;
        ctx.globalCompositeOperation = 'source-over';
        ctx.fillStyle = tColor;
        ctx.font = `${fontSize}px Arial`;
        ctx.textBaseline = 'top';
        ctx.fillText(text, x, y);
      }
    });
    
    ctx.globalCompositeOperation = 'source-over';
  }, [strokes]);

  // Redraw whenever strokes change
  useEffect(() => {
    redrawCanvas();
  }, [redrawCanvas]);

  // Fetch strokes from database and subscribe to changes
  useEffect(() => {
    if (!roomId || !supabase) {
      setLoading(false);
      return;
    }

    const fetchStrokes = async () => {
      const { data, error } = await supabase
        .from('vcafe_whiteboard_strokes')
        .select('*')
        .eq('room_id', roomId)
        .order('order_index', { ascending: true });
      
      if (!error && data) {
        setStrokes(data);
      }
      setLoading(false);
    };
    fetchStrokes();

    // Subscribe to realtime changes (INSERT, UPDATE, DELETE)
    const channel = supabase
      .channel(`whiteboard-${roomId}`)
      .on('postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'vcafe_whiteboard_strokes', filter: `room_id=eq.${roomId}` },
        (payload) => {
          setStrokes(prev => {
            if (prev.some(s => s.id === payload.new.id)) return prev;
            return [...prev, payload.new].sort((a, b) => a.order_index - b.order_index);
          });
        }
      )
      .on('postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'vcafe_whiteboard_strokes', filter: `room_id=eq.${roomId}` },
        (payload) => {
          setStrokes(prev => prev.map(s => s.id === payload.new.id ? payload.new : s));
        }
      )
      .on('postgres_changes',
        { event: 'DELETE', schema: 'public', table: 'vcafe_whiteboard_strokes', filter: `room_id=eq.${roomId}` },
        (payload) => {
          setStrokes(prev => prev.filter(s => s.id !== payload.old.id));
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [roomId, supabase]);

  useEffect(() => {
    if (!roomId || !supabase) return;

    const channel = supabase
      .channel(`whiteboard-pointers-${roomId}`)
      .on('broadcast', { event: 'pointer' }, (payload) => {
        const data = payload?.payload || {};
        const senderId = data.userId;
        if (!senderId || senderId === userId) return;

        setRemotePointers(prev => {
          if (!data.active) {
            const next = { ...prev };
            delete next[senderId];
            return next;
          }
          return {
            ...prev,
            [senderId]: { x: data.x, y: data.y, isPresenter: !!data.isPresenter },
          };
        });
      })
      .subscribe();

    pointerChannelRef.current = channel;

    return () => {
      pointerChannelRef.current = null;
      setRemotePointers({});
      supabase.removeChannel(channel);
    };
  }, [roomId, supabase, userId]);

  const broadcastPointer = (x, y, active) => {
    const channel = pointerChannelRef.current;
    if (!channel || !roomId || !userId) return;
    channel.send({
      type: 'broadcast',
      event: 'pointer',
      payload: {
        x,
        y,
        active,
        userId,
        isPresenter,
      },
    });
  };

  // Save stroke to database (optimistic insert so local user always sees it)
  const saveStroke = async (strokeData) => {
    if (!roomId || !supabase || !userId) return;

    const orderIndex = Date.now(); // Use timestamp for ordering

    const { data, error } = await supabase
      .from('vcafe_whiteboard_strokes')
      .insert({
        room_id: roomId,
        user_id: userId,
        stroke_type: strokeData.stroke_type,
        data: strokeData.data,
        order_index: orderIndex,
      })
      .select('*')
      .single();

    if (error) {
      console.error('Error saving stroke:', error);
      return;
    }

    // Optimistically add to local strokes in case the INSERT event is delayed or dropped
    setStrokes(prev => {
      if (prev.some(s => s.id === data.id)) return prev;
      return [...prev, data].sort((a, b) => a.order_index - b.order_index);
    });
  };

  // Delete stroke from database
  const deleteStroke = async (strokeId) => {
    if (!supabase) return;
    await supabase.from('vcafe_whiteboard_strokes').delete().eq('id', strokeId);
  };

  // Update a shape stroke's data in database and local state
  const updateShapeStroke = async (strokeId, newData) => {
    if (!supabase) return;
    await supabase
      .from('vcafe_whiteboard_strokes')
      .update({ data: newData })
      .eq('id', strokeId);

    // Optimistically update local strokes (we don't subscribe to UPDATE events)
    setStrokes(prev => prev.map(s => s.id === strokeId ? { ...s, data: newData } : s));
  };

  // Clear all strokes for this room
  const clearAllStrokes = async () => {
    if (!roomId || !supabase) return;
    await supabase.from('vcafe_whiteboard_strokes').delete().eq('room_id', roomId);
  };

  const getPos = (e) => {
    const c = canvasRef.current;
    const r = c.getBoundingClientRect();
    const x = (e.clientX ?? (e.touches && e.touches[0]?.clientX)) - r.left;
    const y = (e.clientY ?? (e.touches && e.touches[0]?.clientY)) - r.top;
    // Map from CSS size to internal buffer (keep aspect)
    const sx = c.width / r.width; const sy = c.height / r.height;
    return { x: x * sx, y: y * sy };
  };

  // Hit-test helper for shapes and text (returns stroke if hit, null otherwise)
  const findSelectableAt = (x, y, tolerance = 20) => {
    // Iterate from top-most stroke backwards so we pick the visually top one
    for (let i = strokes.length - 1; i >= 0; i--) {
      const stroke = strokes[i];
      if (!stroke.data) continue;
      
      // Text hit-test
      if (stroke.stroke_type === 'text') {
        const { x: tx, y: ty, text, fontSize } = stroke.data;
        // Approximate text bounding box
        const textWidth = text.length * (fontSize * 0.6);
        const textHeight = fontSize * 1.2;
        if (x >= tx - tolerance && x <= tx + textWidth + tolerance &&
            y >= ty - tolerance && y <= ty + textHeight + tolerance) {
          return stroke;
        }
        continue;
      }
      
      // Shape hit-test
      if (stroke.stroke_type !== 'shape') continue;
      const { shapeType, x0, y0, x1, y1 } = stroke.data;

      if (shapeType === 'line' || shapeType === 'arrow') {
        // Distance from point to line segment
        const dx = x1 - x0; const dy = y1 - y0;
        const lenSq = dx * dx + dy * dy;
        if (lenSq === 0) continue;
        let t = ((x - x0) * dx + (y - y0) * dy) / lenSq;
        t = Math.max(0, Math.min(1, t));
        const px = x0 + t * dx;
        const py = y0 + t * dy;
        const dist = Math.hypot(x - px, y - py);
        if (dist <= tolerance) return stroke;
      } else if (shapeType === 'rect' || shapeType === 'square') {
        const minX = Math.min(x0, x1) - tolerance;
        const maxX = Math.max(x0, x1) + tolerance;
        const minY = Math.min(y0, y1) - tolerance;
        const maxY = Math.max(y0, y1) + tolerance;
        if (x >= minX && x <= maxX && y >= minY && y <= maxY) return stroke;
      } else if (shapeType === 'circle') {
        const cx = (x0 + x1) / 2;
        const cy = (y0 + y1) / 2;
        const r = Math.max(Math.abs(x1 - x0), Math.abs(y1 - y0)) / 2;
        const dist = Math.hypot(x - cx, y - cy);
        if (Math.abs(dist - r) <= tolerance || dist <= r + tolerance) return stroke;
      }
    }
    return null;
  };

  // Draw shape helper
  const drawShapeOnCanvas = (ctx, shapeType, x0, y0, x1, y1, opts = {}) => {
    const { fill = false, constrain = false } = opts;
    if (shapeType === 'line' || shapeType === 'arrow') {
      let dx = x1 - x0, dy = y1 - y0;
      if (constrain) {
        const angle = Math.atan2(dy, dx);
        const step = Math.PI / 4;
        const snapped = Math.round(angle / step) * step;
        const len = Math.hypot(dx, dy);
        dx = Math.cos(snapped) * len;
        dy = Math.sin(snapped) * len;
        x1 = x0 + dx; y1 = y0 + dy;
      }
      ctx.beginPath(); ctx.moveTo(x0, y0); ctx.lineTo(x1, y1); ctx.stroke();
      if (shapeType === 'arrow') {
        const headLen = Math.max(10, ctx.lineWidth * 3);
        const ang = Math.atan2(y1 - y0, x1 - x0);
        ctx.beginPath();
        ctx.moveTo(x1, y1);
        ctx.lineTo(x1 - headLen * Math.cos(ang - Math.PI / 6), y1 - headLen * Math.sin(ang - Math.PI / 6));
        ctx.moveTo(x1, y1);
        ctx.lineTo(x1 - headLen * Math.cos(ang + Math.PI / 6), y1 - headLen * Math.sin(ang + Math.PI / 6));
        ctx.stroke();
      }
    } else if (shapeType === 'rect' || shapeType === 'square') {
      let w = x1 - x0; let h = y1 - y0;
      if (shapeType === 'square' || constrain) {
        const s = Math.max(Math.abs(w), Math.abs(h));
        w = Math.sign(w || 1) * s;
        h = Math.sign(h || 1) * s;
      }
      if (fill) ctx.fillRect(x0, y0, w, h);
      else ctx.strokeRect(x0, y0, w, h);
    } else if (shapeType === 'circle') {
      const w = x1 - x0; const h = y1 - y0;
      const r = Math.max(Math.abs(w), Math.abs(h)) / 2;
      const cx = x0 + w / 2; const cy = y0 + h / 2;
      ctx.beginPath(); ctx.arc(cx, cy, r, 0, Math.PI * 2);
      if (fill) ctx.fill(); else ctx.stroke();
    }
  };

  const onDown = (e) => {
    e.preventDefault();
    const c = canvasRef.current;
    const ctx = c.getContext('2d');
    const { x, y } = getPos(e);

    // Pointer click+hold for audience and explicit pointer tool for presenter
    if (!isPresenter || tool === 'pointer') {
      setPointerPos({ x, y });
      isPointerDownRef.current = true;
      broadcastPointer(x, y, true);
      if (!isPresenter) {
        // Audience only has pointer interactions
        return;
      }
    }

    if (!isPresenter) return;

    if (tool === 'select') {
      // Try to pick an existing shape or text to drag
      const hit = findSelectableAt(x, y);
      if (hit) {
        setSelectedShapeId(hit.id);
        dragShapeRef.current = {
          stroke: hit,
          startMouse: { x, y },
        };
        setDrawing(true);
      } else {
        setSelectedShapeId(null);
        dragShapeRef.current = null;
      }
      return;
    }

    if (tool === 'pointer') {
      setPointerPos({ x, y });
      return;
    }
    
    if (tool === 'pen' || tool === 'eraser') {
      setDrawing(true);
      currentStrokeRef.current = [{ x, y }];
      // Draw live preview
      ctx.lineCap = 'round'; ctx.lineJoin = 'round'; ctx.lineWidth = size;
      if (tool === 'eraser') {
        ctx.globalCompositeOperation = 'destination-out';
        ctx.strokeStyle = 'rgba(0,0,0,1)';
      } else {
        ctx.globalCompositeOperation = 'source-over';
        ctx.strokeStyle = color;
      }
      ctx.beginPath(); ctx.moveTo(x, y);
    } else if (tool === 'text') {
      setTextBox({ active: true, x, y, text: '', color });
    } else {
      // Shape tools
      startRef.current = { x, y };
      setDrawing(true);
    }
  };

  const onMove = (e) => {
    const isPointerMode = (tool === 'pointer') || !isPresenter;
    if (isPointerMode) {
      const { x, y } = getPos(e);
      setPointerPos({ x, y });
      if (tool === 'pointer' || isPointerDownRef.current) {
        broadcastPointer(x, y, true);
      }
      return;
    }

    if (tool === 'select' && drawing && dragShapeRef.current && dragShapeRef.current.stroke) {
      const { x, y } = getPos(e);
      const { stroke, startMouse } = dragShapeRef.current;
      const dx = x - startMouse.x;
      const dy = y - startMouse.y;

      const c = canvasRef.current; const ctx = c.getContext('2d');
      redrawCanvas();
      
      // Draw preview based on stroke type
      if (stroke.stroke_type === 'text') {
        const { x: tx, y: ty, text, color: tColor, fontSize } = stroke.data;
        ctx.globalCompositeOperation = 'source-over';
        ctx.fillStyle = tColor;
        ctx.font = `${fontSize}px Arial`;
        ctx.textBaseline = 'top';
        ctx.fillText(text, tx + dx, ty + dy);
      } else if (stroke.stroke_type === 'shape') {
        const { shapeType, x0, y0, x1, y1, color: sColor, size: sSize, fill } = stroke.data;
        const nx0 = x0 + dx;
        const ny0 = y0 + dy;
        const nx1 = x1 + dx;
        const ny1 = y1 + dy;
        ctx.strokeStyle = sColor;
        ctx.fillStyle = sColor;
        ctx.lineWidth = sSize;
        ctx.lineCap = 'round'; ctx.lineJoin = 'round';
        drawShapeOnCanvas(ctx, shapeType, nx0, ny0, nx1, ny1, { fill });
      }
      return;
    }

    if (!drawing || !isPresenter) return;
    const c = canvasRef.current; const ctx = c.getContext('2d');
    const { x, y } = getPos(e);
    
    if (tool === 'pen' || tool === 'eraser') {
      currentStrokeRef.current.push({ x, y });
      ctx.lineTo(x, y); ctx.stroke();
      return;
    }
    
    // Live preview for shapes
    if (startRef.current) {
      redrawCanvas();
      ctx.strokeStyle = color;
      ctx.fillStyle = color;
      ctx.lineWidth = shapeSize;
      ctx.lineCap = 'round'; ctx.lineJoin = 'round';
      const { x: x0, y: y0 } = startRef.current;
      const constrain = !!(e.shiftKey);
      drawShapeOnCanvas(ctx, tool, x0, y0, x, y, { constrain, fill: fillShapes });
    }
  };

  const onUp = (e) => {
    // Stop any pointer broadcast on mouse/touch release
    if (isPointerDownRef.current) {
      isPointerDownRef.current = false;
      broadcastPointer(pointerPos?.x ?? 0, pointerPos?.y ?? 0, false);
    }

    if (!drawing || !isPresenter) return;

    const c = canvasRef.current;
    const ctx = c.getContext('2d');
    
    if (tool === 'select' && dragShapeRef.current && dragShapeRef.current.stroke && selectedShapeId) {
      // Finalize drag of existing shape or text
      const { x, y } = getPos(e || {});
      const { stroke, startMouse } = dragShapeRef.current;
      const dx = x - startMouse.x;
      const dy = y - startMouse.y;
      
      let newData;
      if (stroke.stroke_type === 'text') {
        const { x: tx, y: ty, text, color: tColor, fontSize } = stroke.data;
        newData = {
          x: tx + dx,
          y: ty + dy,
          text,
          color: tColor,
          fontSize,
        };
      } else if (stroke.stroke_type === 'shape') {
        const { shapeType, x0, y0, x1, y1, color: sColor, size: sSize, fill } = stroke.data;
        newData = {
          shapeType,
          x0: x0 + dx,
          y0: y0 + dy,
          x1: x1 + dx,
          y1: y1 + dy,
          color: sColor,
          size: sSize,
          fill,
        };
      }

      if (newData) {
        redrawCanvas();
        updateShapeStroke(stroke.id, newData);
      }

      dragShapeRef.current = null;
      setDrawing(false);
      return;
    }

    // Save pen/eraser stroke to database
    if (tool === 'pen' || tool === 'eraser') {
      if (currentStrokeRef.current.length > 1) {
        saveStroke({
          stroke_type: tool,
          data: {
            points: currentStrokeRef.current,
            color: color,
            size: size,
          }
        });
      }
      currentStrokeRef.current = [];
    } else if (startRef.current) {
      // Shape: commit immediately on mouse up (no Place/Cancel overlay)
      const { x, y } = getPos(e || {});
      const { x: x0, y: y0 } = startRef.current;
      const constrain = !!(e && e.shiftKey);

      // Draw final shape onto canvas
      redrawCanvas();
      ctx.strokeStyle = color;
      ctx.fillStyle = color;
      ctx.lineWidth = shapeSize;
      ctx.lineCap = 'round'; ctx.lineJoin = 'round';
      drawShapeOnCanvas(ctx, tool, x0, y0, x, y, { constrain, fill: fillShapes });

      // Persist to database so audience sees it
      saveStroke({
        stroke_type: 'shape',
        data: {
          shapeType: tool,
          x0,
          y0,
          x1: x,
          y1: y,
          color,
          size: shapeSize,
          fill: fillShapes,
        }
      });

      startRef.current = null;
    }
    
    setDrawing(false);
  };

  // Commit text to database
  const commitText = () => {
    if (!textBox.text.trim()) {
      setTextBox({ active: false, x: 40, y: 40, text: '', color: '#000000' });
      return;
    }
    saveStroke({
      stroke_type: 'text',
      data: {
        x: textBox.x,
        y: textBox.y,
        text: textBox.text,
        color: textBox.color,
        fontSize: 14 + size * 2,
      }
    });
    setTextBox({ active: false, x: 40, y: 40, text: '', color: '#000000' });
  };

  // Clear all strokes (database)
  const clearBoard = () => {
    clearAllStrokes();
  };

  // Undo: delete last stroke from database
  const undo = () => {
    if (strokes.length === 0) return;
    const lastStroke = strokes[strokes.length - 1];
    deleteStroke(lastStroke.id);
  };

  // Redo is not supported with database sync (would need separate redo table)
  const redo = () => {
    // Not implemented - would need to track deleted strokes
  };

  let style;

  if (layoutMode === 'fullscreen') {
    // Standalone fullscreen whiteboard
    style = {
      border: '1px solid var(--border-light, #2a3141)',
      background: '#ffffff',
      borderRadius: 8,
      width: '90vw',
      height: '85vh',
      maxWidth: '90vw',
      maxHeight: '85vh',
      aspectRatio: 'auto',
    };
  } else if (layoutMode === 'presentation') {
    // Inside unified room layout: take the full main content area
    style = {
      border: '1px solid var(--border-light, #2a3141)',
      background: '#fff',
      borderRadius: 8,
      width: '100%',
      height: '100%',
      maxWidth: '100%',
      maxHeight: '100%',
      aspectRatio: 'auto',
    };
  } else {
    // Default embedded whiteboard (e.g. cards/sections)
    style = {
      border: '1px solid var(--border-light, #2a3141)',
      background: '#fff',
      borderRadius: 8,
      width: '100%',
      height: expanded ? 'min(700px, 60vh)' : 'min(500px, 50vh)',
      maxWidth: expanded ? '1000px' : '800px',
      maxHeight: 'none',
      aspectRatio: '4 / 3',
    };
  }


  // Global listeners for dragging the textbox so it never "escapes" the cursor
  useEffect(() => {
    const onMove = (ev) => {
      if (!textDragRef.current.active) return;
      const wrapRect = canvasRef.current?.parentElement?.getBoundingClientRect();
      const canvasRect2 = canvasRef.current?.getBoundingClientRect();
      if (!wrapRect || !canvasRect2) return;
      const cssW = canvasRect2.width; const cssH = canvasRect2.height;
      const nxCss = ev.clientX - wrapRect.left - textDragRef.current.dx;
      const nyCss = ev.clientY - wrapRect.top - textDragRef.current.dy;
      const nx = (nxCss / cssW) * 1200; const ny = (nyCss / cssH) * 800;
      setTextBox(tb => ({ ...tb, x: Math.max(0, Math.min(1200, nx)), y: Math.max(0, Math.min(800, ny)) }));
    };
    const onUp = () => { textDragRef.current.active = false; };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, []);

  // For textbox overlay positioning, prefer actual canvas CSS size
  const canvasRect = canvasRef.current ? canvasRef.current.getBoundingClientRect() : null;
  const cssW = canvasRect ? canvasRect.width : (parseFloat(style.width) || 800);
  const cssH = canvasRect ? canvasRect.height : (parseFloat(style.height) || 500);

  const rootClassName = layoutMode === 'presentation'
    ? 'cafe-whiteboard cafe-whiteboard--presentation'
    : 'cafe-whiteboard';

  // Audience always uses pointer cursor/behavior; presenters use active tool
  const canvasTool = isPresenter ? tool : 'pointer';

  return (
    <>
      <div className={rootClassName} style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {isPresenter && (
          <div className="wb-controls" role="toolbar" aria-label="Whiteboard controls">
          {/* Pointer tool */}
          <button className={`wb-btn ${tool === 'pointer' ? 'active' : ''}`} title="Pointer" onClick={() => { setTool('pointer'); setShowPenMenu(false); setShowEraserMenu(false); setShowShapesMenu(false); }}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M3 3l7.07 16.97 2.51-7.39 7.39-2.51L3 3z" />
            </svg>
          </button>
          <div style={{ position: 'relative' }}>
            <button className={`wb-btn ${tool === 'pen' ? 'active' : ''}`} title="Pen" onClick={() => { setTool('pen'); setShowPenMenu(s => !s); setShowEraserMenu(false); }}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 20h9" />
                <path d="M16.5 3.5l4 4L7 21H3v-4L16.5 3.5z" />
              </svg>
            </button>
            {showPenMenu && (
              <div className="wb-popover">
                {[2, 4, 8, 12, 20].map(s => (
                  <button key={s} onClick={() => { setSize(s); setTool('pen'); setShowPenMenu(false); }} title={`Pen ${s}px`} className="wb-swatch">
                    <span style={{ height: s, background: color }} />
                  </button>
                ))}
              </div>
            )}
          </div>
          <div style={{ position: 'relative' }}>
            <button className={`wb-btn ${tool === 'eraser' ? 'active' : ''}`} title="Eraser" onClick={() => { setTool('eraser'); setShowEraserMenu(s => !s); setShowPenMenu(false); }}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M16 3l5 5-9 9H7l-4-4 9-9z" />
                <path d="M6 17h7" />
              </svg>
            </button>
            {showEraserMenu && (
              <div className="wb-popover">
                {[6, 10, 16, 24, 32].map(s => (
                  <button key={s} onClick={() => { setSize(s); setTool('eraser'); setShowEraserMenu(false); }} title={`Eraser ${s}px`} className="wb-swatch">
                    <span style={{ height: s, background: 'linear-gradient(90deg, rgba(0,0,0,0) 0%, rgba(255,255,255,1) 100%)' }} />
                  </button>
                ))}
              </div>
            )}
          </div>
          <div style={{ position: 'relative' }}>
            <button className={`wb-btn ${(tool === 'line' || tool === 'rect' || tool === 'square' || tool === 'circle' || tool === 'arrow') ? 'active' : ''}`} title="Shapes" onClick={() => { setShowShapesMenu(s => !s); setShowPenMenu(false); setShowEraserMenu(false); }}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="3" width="7" height="7" rx="1" />
                <circle cx="17" cy="7" r="3" />
                <path d="M14 14l7 7H7z" />
              </svg>
            </button>
            {showShapesMenu && (
              <div className="wb-popover">
                {[
                  { k: 'line', label: '／', title: 'Line' },
                  { k: 'rect', label: '▭', title: 'Rectangle' },
                  { k: 'square', label: '◼︎', title: 'Square' },
                  { k: 'circle', label: '◯', title: 'Circle' },
                  { k: 'arrow', label: '➤', title: 'Arrow' }
                ].map(s => (
                  <button key={s.k} onClick={() => { setTool(s.k); setShowShapesMenu(false); }} title={s.title} className="wb-swatch">{s.label}</button>
                ))}
              </div>
            )}
            <input
              type="range"
              min={1}
              max={40}
              value={shapeSize}
              onChange={(e) => setShapeSize(Number(e.target.value) || 1)}
              className="wb-shape-size-slider"
              title="Shape width"
            />
          </div>
          {/* Select tool (shapes and text) */}
          <button
            className={`wb-btn ${tool === 'select' ? 'active' : ''}`}
            title="Select / move"
            onClick={() => { setTool('select'); setShowPenMenu(false); setShowEraserMenu(false); setShowShapesMenu(false); setSelectedShapeId(null); }}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              {/* Dotted square */}
              <rect x="5" y="5" width="14" height="14" rx="2" strokeDasharray="3 2" />
              {/* 4-way arrows */}
              <path d="M12 8v8M8 12h8" />
              <path d="M12 8l-2 2M12 8l2 2" />
              <path d="M12 16l-2-2M12 16l2-2" />
              <path d="M8 12l2-2M8 12l2 2" />
              <path d="M16 12l-2-2M16 12l-2 2" />
            </svg>
          </button>
          <button className={`wb-btn ${tool === 'text' ? 'active' : ''}`} title="Text" onClick={() => { setTool('text'); setShowPenMenu(false); setShowEraserMenu(false); }}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M4 6h16" />
              <path d="M12 6v12" />
            </svg>
          </button>
          <label title="Color" className="wb-color"><input type="color" value={color} onChange={(e) => setColor(e.target.value)} /></label>
          <label title="Fill shapes" className="wb-fill">
            <input type="checkbox" checked={fillShapes} onChange={(e) => setFillShapes(e.target.checked)} /> Fill
          </label>
          <div className="toolbar-spacer" />
          <button onClick={undo} title="Undo" className="wb-btn">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M3 7v6h6" />
              <path d="M3 13a9 9 0 1 0 3-6" />
            </svg>
          </button>
          <button onClick={redo} title="Redo" className="wb-btn">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 7v6h-6" />
              <path d="M21 13a9 9 0 1 1-3-6" />
            </svg>
          </button>
          <button onClick={clearBoard} title="Clear" className="wb-btn">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M3 6h18" />
              <path d="M8 6V4h8v2" />
              <path d="M19 6l-1 14H6L5 6" />
            </svg>
          </button>
          <button onClick={() => setExpanded(v => !v)} title={expanded ? 'Collapse' : 'Expand'} className="wb-btn">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M15 3h6v6" />
              <path d="M21 3l-7 7" />
              <path d="M9 21H3v-6" />
              <path d="M3 21l7-7" />
            </svg>
          </button>
          {onEnterFocusMode && (
            <>
              <button onClick={() => onEnterFocusMode('whiteboard', 'fullscreen')} title="Fullscreen" className="wb-btn">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3" />
                </svg>
              </button>
            </>
          )}
        </div>
        )}
        <div className={`canvas-wrap tool-${canvasTool}${drawing && canvasTool === 'select' ? ' dragging' : ''}`} style={{ position: 'relative', width: style.width, height: style.height }}>
          <canvas
            ref={canvasRef}
            style={style}
            onMouseDown={onDown}
            onMouseMove={onMove}
            onMouseUp={onUp}
            onMouseLeave={onUp}
            onTouchStart={onDown}
            onTouchMove={onMove}
            onTouchEnd={onUp}
          />
          {/* Selection highlight overlay */}
          {tool === 'select' && selectedShapeId && (() => {
            const selected = strokes.find(s => s.id === selectedShapeId);
            if (!selected || !selected.data) return null;
            let bounds = null;
            if (selected.stroke_type === 'text') {
              const { x, y, text, fontSize } = selected.data;
              const textWidth = text.length * (fontSize * 0.6);
              const textHeight = fontSize * 1.2;
              bounds = { x, y, w: textWidth, h: textHeight };
            } else if (selected.stroke_type === 'shape') {
              const { shapeType, x0, y0, x1, y1 } = selected.data;
              if (shapeType === 'line' || shapeType === 'arrow') {
                bounds = { x: Math.min(x0, x1), y: Math.min(y0, y1), w: Math.abs(x1 - x0), h: Math.abs(y1 - y0) };
              } else {
                bounds = { x: Math.min(x0, x1), y: Math.min(y0, y1), w: Math.abs(x1 - x0), h: Math.abs(y1 - y0) };
              }
            }
            if (!bounds) return null;
            const pad = 6;
            return (
              <div
                className="wb-selection-highlight"
                style={{
                  left: (bounds.x / 1200) * cssW - pad,
                  top: (bounds.y / 800) * cssH - pad,
                  width: (bounds.w / 1200) * cssW + pad * 2,
                  height: (bounds.h / 800) * cssH + pad * 2,
                }}
              />
            );
          })()}
          {/* Local pointer indicator (presenter pointer tool or audience hover) */}
          {((tool === 'pointer') || !isPresenter) && pointerPos && (() => {
            const isAudienceLocal = !isPresenter;
            const isAudienceActive = isAudienceLocal && isPointerDownRef.current;
            const size = 18;
            const radiusOffset = size / 2;
            const borderColor = isAudienceLocal
              ? (isAudienceActive ? 'rgba(239,68,68,0.95)' : 'rgba(239,68,68,0.6)')
              : 'rgba(239,68,68,0.9)';
            const background = isAudienceLocal && !isAudienceActive
              ? 'transparent'
              : 'rgba(239,68,68,0.08)';
            return (
              <div
                className="wb-pointer-indicator"
                style={{
                  position: 'absolute',
                  left: (pointerPos.x / 1200) * cssW - radiusOffset,
                  top: (pointerPos.y / 800) * cssH - radiusOffset,
                  width: size,
                  height: size,
                  borderRadius: '999px',
                  border: `2px solid ${borderColor}`,
                  background,
                  boxShadow: 'none',
                  pointerEvents: 'none',
                  zIndex: 10,
                  animation: 'none',
                }}
              />
            );
          })()}
          {/* Remote pointers: presenter ones are always shown while active; audience ones only when click+hold is active */}
          {Object.entries(remotePointers).map(([uid, p]) => (
            <div
              key={uid}
              className="wb-pointer-indicator"
              style={{
                position: 'absolute',
                left: (p.x / 1200) * cssW - 10,
                top: (p.y / 800) * cssH - 10,
                width: 20,
                height: 20,
                borderRadius: '999px',
                background: p.isPresenter
                  ? 'radial-gradient(circle, rgba(59,130,246,0.95) 0%, rgba(59,130,246,0.4) 70%, transparent 100%)'
                  : 'radial-gradient(circle, rgba(14,165,233,0.95) 0%, rgba(14,165,233,0.4) 70%, transparent 100%)',
                boxShadow: p.isPresenter
                  ? '0 0 18px rgba(59,130,246,0.9)'
                  : '0 0 14px rgba(14,165,233,0.8)',
                pointerEvents: 'none',
                zIndex: 9,
                animation: 'wb-pointer-pulse 1s ease-in-out infinite',
              }}
            />
          ))}
          {textBox.active && (
            <div
              className="wb-textbox"
              style={{ position: 'absolute', left: textBox.x * (cssW / 1200), top: textBox.y * (cssH / 800), background: '#fff', border: '1px solid var(--border-light, rgba(0,0,0,0.2))', borderRadius: 6, padding: 6, boxShadow: '0 2px 8px rgba(0,0,0,0.25)', minWidth: 160, zIndex: 3, cursor: 'move' }}
              onMouseDown={(ev) => { draggingRef.current = true; const rect = ev.currentTarget.getBoundingClientRect(); dragOffsetRef.current = { dx: ev.clientX - rect.left, dy: ev.clientY - rect.top }; ev.stopPropagation(); }}
              onMouseUp={() => { draggingRef.current = false; }}
              onMouseMove={(ev) => { if (!draggingRef.current) return; const wrap = ev.currentTarget.parentElement.getBoundingClientRect(); const canvasRect = canvasRef.current?.getBoundingClientRect(); const cssW = canvasRect?.width || parseFloat(style.width) || 800; const cssH = canvasRect?.height || parseFloat(style.height) || 500; const nxCss = ev.clientX - wrap.left - dragOffsetRef.current.dx; const nyCss = ev.clientY - wrap.top - dragOffsetRef.current.dy; const nx = (nxCss / cssW) * 1200; const ny = (nyCss / cssH) * 800; setTextBox(tb => ({ ...tb, x: Math.max(0, Math.min(1200, nx)), y: Math.max(0, Math.min(800, ny)) })); }}
            >
              <textarea
                autoFocus
                value={textBox.text}
                onChange={(ev) => setTextBox(tb => ({ ...tb, text: ev.target.value }))}
                placeholder="Type here..."
                style={{ width: 240, minHeight: 64, border: 'none', outline: 'none', resize: 'both', fontSize: 14 + size * 2, color: textBox.color }}
              />
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 6 }}>
                <label className="wb-color" title="Text color"><input type="color" value={textBox.color} onChange={(e) => setTextBox(tb => ({ ...tb, color: e.target.value }))} /></label>
                <div className="toolbar-spacer" />
                <button onClick={() => setTextBox({ active: false, x: 40, y: 40, text: '', color: '#000000' })} title="Cancel" className="wb-small">✖</button>
                <button onClick={commitText} title="Place" className="wb-small">✔</button>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default CafeWhiteboard;